import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-article-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            image: article.coverImage,
            url: `https://www.douhuavideo.com/article/${article.id}`,
            inLanguage: 'zh-CN',
            wordCount: article.content.length,
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '豆花视频首页',
                item: 'https://www.douhuavideo.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '文章',
                item: 'https://www.douhuavideo.com/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: '文章', href: '/' },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-10">
            <div className="max-w-4xl mx-auto">
              {/* Article Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                    {article.category}
                  </span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-background-50 border border-background-200/70 text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>

              {/* Cover Image */}
              <div className="rounded-xl overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none mb-12 article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <style>{`
                .article-content h3 {
                  font-size: 1.25rem;
                  font-weight: 700;
                  color: oklch(var(--foreground-900));
                  margin-top: 2rem;
                  margin-bottom: 0.75rem;
                }
                .article-content p {
                  font-size: 1rem;
                  line-height: 1.85;
                  color: oklch(var(--foreground-700));
                  margin-bottom: 1.25rem;
                }
                .article-content strong {
                  color: oklch(var(--foreground-900));
                }
              `}</style>

              {/* Share */}
              <div className="flex items-center gap-3 py-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-600">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-colors cursor-pointer">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-colors cursor-pointer">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-colors cursor-pointer">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="space-y-4">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}